import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { offersAPI } from '../utils/api';
import { DiscountApprovalHistoryItem } from '../types';
import toast from 'react-hot-toast';
import { CheckIcon, XMarkIcon, EyeIcon, ClockIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

type TabType = 'pending' | 'history';

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DiscountApprovals: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [page, setPage] = useState(1);
  const [rejectModalOffer, setRejectModalOffer] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const limit = 20;

  // Richieste in attesa
  const { data: pendingData, isLoading: isLoadingPending } = useQuery({
    queryKey: ['discount-approvals', 'pending', page],
    queryFn: () => offersAPI.getPendingApproval({ page, limit }),
  });

  // Storico richieste processate
  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['discount-approvals', 'history', page],
    queryFn: async () => (await offersAPI.getDiscountApprovalsHistory({ page, limit })).data,
    enabled: activeTab === 'history',
  });

  const approveMutation = useMutation({
    mutationFn: (offerId: string) => offersAPI.approveDiscount(offerId),
    onSuccess: () => {
      toast.success('Sconti approvati con successo');
      queryClient.invalidateQueries({ queryKey: ['discount-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals-count'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'approvazione');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ offerId, note }: { offerId: string; note?: string }) => offersAPI.rejectDiscount(offerId, note),
    onSuccess: () => {
      toast.success('Sconti rifiutati');
      setRejectModalOffer(null);
      setRejectNote('');
      queryClient.invalidateQueries({ queryKey: ['discount-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals-count'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il rifiuto');
    },
  });

  const pendingOffers = pendingData?.data?.data || [];
  const pendingTotal = pendingData?.data?.pagination?.total || 0;
  const historyItems: DiscountApprovalHistoryItem[] = historyData?.data || [];
  const historyPagination = historyData?.pagination;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Approvazioni Sconto</h1>
        <p className="text-sm text-gray-600 mt-0.5">
          Richieste di autorizzazione agli sconti oltre limite inviate dai venditori
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => { setActiveTab('pending'); setPage(1); }}
            className={`flex items-center py-2.5 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClockIcon className="h-4 w-4 mr-1.5" />
            In attesa
            {pendingTotal > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-primary-600 rounded-full">
                {pendingTotal}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('history'); setPage(1); }}
            className={`flex items-center py-2.5 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CheckBadgeIcon className="h-4 w-4 mr-1.5" />
            Storico
          </button>
        </nav>
      </div>

      {/* Tab: In attesa */}
      {activeTab === 'pending' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoadingPending ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : pendingOffers.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <CheckBadgeIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nessuna richiesta di approvazione in attesa</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Offerta</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Totale</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pendingOffers.map((offer: any) => (
                  <tr key={offer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <div className="text-sm font-medium text-gray-900">{offer.numero}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[260px]">{offer.titolo}</div>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">
                      {offer.cliente?.ragione_sociale || `${offer.cliente?.nome || ''} ${offer.cliente?.cognome || ''}`.trim() || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium text-gray-900">
                      €{Number(offer.importo_totale || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{formatDateTime(offer.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`/offers/${offer.id}/edit`)}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                          title="Apri l'offerta"
                        >
                          <EyeIcon className="h-3.5 w-3.5 mr-1" />
                          Apri
                        </button>
                        <button
                          onClick={() => approveMutation.mutate(offer.id)}
                          disabled={approveMutation.isPending}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs border border-transparent rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckIcon className="h-3.5 w-3.5 mr-1" />
                          Approva
                        </button>
                        <button
                          onClick={() => setRejectModalOffer(offer.id)}
                          disabled={rejectMutation.isPending}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                        >
                          <XMarkIcon className="h-3.5 w-3.5 mr-1" />
                          Rifiuta
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab: Storico */}
      {activeTab === 'history' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoadingHistory ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : historyItems.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nessuna richiesta processata finora</p>
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Richiesta</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Offerta</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venditore</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Esito</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nota</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risposta</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {historyItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(item.richiesta_at)}</td>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => navigate(`/offers/${item.id}/edit`)}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700"
                        >
                          {item.numero}
                        </button>
                        <div className="text-xs text-gray-500 truncate max-w-[220px]">{item.titolo}</div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-700">{item.cliente_nome || '-'}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-700">{item.venditore_nome || '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.approvazione_stato === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {item.approvazione_stato === 'approved' ? 'Approvata' : 'Rifiutata'}
                        </span>
                        {item.approvato_da && (
                          <div className="text-xs text-gray-400 mt-0.5">da {item.approvato_da}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[220px] truncate" title={item.approvazione_note || ''}>
                        {item.approvazione_note || '-'}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(item.risposta_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Paginazione */}
              {historyPagination && historyPagination.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Pagina {historyPagination.page} di {historyPagination.pages} ({historyPagination.total} richieste)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Precedente
                    </button>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= historyPagination.pages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Successiva
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal rifiuto */}
      {rejectModalOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-base font-medium text-gray-900 mb-1">Rifiuta richiesta sconto</h3>
            <p className="text-sm text-gray-500 mb-3">
              Il venditore riceverà una notifica con la motivazione del rifiuto.
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={3}
              placeholder="Motivazione del rifiuto (opzionale)"
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setRejectModalOffer(null); setRejectNote(''); }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => rejectMutation.mutate({ offerId: rejectModalOffer, note: rejectNote || undefined })}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Invio...' : 'Conferma rifiuto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountApprovals;
